import React, { useContext, useState } from 'react';
import { Layout } from './Layout';
import { LLMModelSettings } from './settings/LLMModelSettings';
import { AuditWorkflowSettings } from './settings/AuditWorkflowSettings';
import { RedactionPolicySettings } from './settings/RedactionPolicySettings';
import { UserManagementSettings } from './settings/UserManagementSettings';
import { SystemSettings } from './settings/SystemSettings';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from './ui/breadcrumb';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import { Home, Settings as SettingsIcon, Database, FileX, Shield, Users, Cog } from 'lucide-react';
import { AppContext } from '@/App';

export interface SettingsConfig {
    llmModel: {
        selectedModel: string;
        temperature: number;
        maxTokens: number;
        autoApproveThreshold: number;
        autoFlagThreshold: number;
        currentPromptVersion: string;
        // Extended API configuration
        providers: {
            openai: {
                apiKey: string;
                organization: string;
                baseUrl: string;
                enabled: boolean;
            };
            anthropic: {
                apiKey: string;
                baseUrl: string;
                enabled: boolean;
            };
            azure: {
                apiKey: string;
                endpoint: string;
                deploymentName: string;
                apiVersion: string;
                enabled: boolean;
            };
            custom: {
                apiKey: string;
                baseUrl: string;
                name: string;
                enabled: boolean;
            };
        };
        fallbackProvider: string;
        retryAttempts: number;
        requestTimeout: number;
        enableLogging: boolean;
        logLevel: string;
        enableCaching: boolean;
        cacheExpiry: number;
        rateLimits: {
            requestsPerMinute: number;
            tokensPerMinute: number;
        };
    };
    auditWorkflow: {
        requireCommentOnReject: boolean;
        maxAuditsPerAuditor: number;
        autoAssignBySpecialty: boolean;
        emailNotificationsEnabled: boolean;
        escalationAfterDays: number;
    };
    redactionPolicy: {
        redactPatientNames: boolean;
        redactMRNumbers: boolean;
        redactPhoneNumbers: boolean;
        redactAddresses: boolean;
        redactDates: boolean;
        redactClinicianNames: boolean;
        customPatterns: string[];
    };
    userManagement: {
        defaultRole: string;
        requireTwoFactor: boolean;
        passwordExpiryDays: number;
        maxLoginAttempts: number;
    };
    system: {
        auditLogRetentionDays: number;
        enableDetailedLogging: boolean;
        dataBackupEnabled: boolean;
        maintenanceMode: boolean;
        apiRateLimitPerHour: number;
        chartConfigurations: Array<{
            chartType: 'CSC' | 'DLS' | 'AIDE' | 'SN';
            reviewNeeded: boolean;
            description: string;
            isActive: boolean;
        }>;
    };
}

// Mock initial settings configuration
const initialSettings: SettingsConfig = {
    llmModel: {
        selectedModel: 'gpt-4-turbo',
        temperature: 0.3,
        maxTokens: 2048,
        autoApproveThreshold: 90,
        autoFlagThreshold: 60,
        currentPromptVersion: 'GoalEvaluation v2.3 (active)',
        providers: {
            openai: {
                apiKey: '',
                organization: '',
                baseUrl: 'https://api.openai.com/v1',
                enabled: true,
            },
            anthropic: {
                apiKey: '',
                baseUrl: 'https://api.anthropic.com/v1',
                enabled: false,
            },
            azure: {
                apiKey: '',
                endpoint: '',
                deploymentName: '',
                apiVersion: '2024-02-01',
                enabled: false,
            },
            custom: {
                apiKey: '',
                baseUrl: '',
                name: 'Custom Provider',
                enabled: false,
            },
        },
        fallbackProvider: 'openai',
        retryAttempts: 3,
        requestTimeout: 30,
        enableLogging: true,
        logLevel: 'info',
        enableCaching: true,
        cacheExpiry: 3600,
        rateLimits: {
            requestsPerMinute: 60,
            tokensPerMinute: 90000,
        },
    },
    auditWorkflow: {
        requireCommentOnReject: true,
        maxAuditsPerAuditor: 50,
        autoAssignBySpecialty: false,
        emailNotificationsEnabled: true,
        escalationAfterDays: 7,
    },
    redactionPolicy: {
        redactPatientNames: true,
        redactMRNumbers: true,
        redactPhoneNumbers: true,
        redactAddresses: true,
        redactDates: false,
        redactClinicianNames: false,
        customPatterns: ['SSN', 'Social Security'],
    },
    userManagement: {
        defaultRole: 'Auditor',
        requireTwoFactor: false,
        passwordExpiryDays: 90,
        maxLoginAttempts: 5,
    },
    system: {
        auditLogRetentionDays: 365,
        enableDetailedLogging: true,
        dataBackupEnabled: true,
        maintenanceMode: false,
        apiRateLimitPerHour: 1000,
        chartConfigurations: [
            {
                chartType: 'SN',
                reviewNeeded: false,
                description: 'SN charts do not require auditor review and will be auto-approved',
                isActive: true
            },
            {
                chartType: 'CSC',
                reviewNeeded: true,
                description: 'Complex Care charts require mandatory validation',
                isActive: true
            },
            {
                chartType: 'DLS',
                reviewNeeded: true,
                description: 'Daily Living Services require supervisor sign-off',
                isActive: true
            },
            {
                chartType: 'AIDE',
                reviewNeeded: false,
                description: 'Standard aide services are auto-processed',
                isActive: true
            }
        ]
    },
};

export function Settings() {
    const [settings, setSettings] = useState<SettingsConfig>(initialSettings);
    const [activeTab, setActiveTab] = useState('system');
    const [unsavedChanges, setUnsavedChanges] = useState<Set<string>>(new Set());
    const { setCurrentView } = useContext(AppContext);



    const handleSettingsUpdate = (section: keyof SettingsConfig, newSettings: any) => {
        setSettings(prev => ({
            ...prev,
            [section]: { ...prev[section], ...newSettings }
        }));

        setUnsavedChanges(prev => new Set(prev).add(section));
    };

    const handleSettingsSave = (section: keyof SettingsConfig) => {
        // In a real app, this would save to the backend
        console.log(`Saving ${section} settings:`, settings[section]);

        setUnsavedChanges(prev => {
            const newSet = new Set(prev);
            newSet.delete(section);
            return newSet;
        });
    };

    const tabs = [
        {
            id: 'llm-model',
            label: 'LLM Model',
            icon: Database,
            hasUnsavedChanges: unsavedChanges.has('llmModel'),
        },
        {
            id: 'audit-workflow',
            label: 'Audit Rules',
            icon: FileX,
            hasUnsavedChanges: unsavedChanges.has('auditWorkflow'),
        },
        {
            id: 'redaction-policy',
            label: 'Redaction Policy',
            icon: Shield,
            hasUnsavedChanges: unsavedChanges.has('redactionPolicy'),
        },
        {
            id: 'user-management',
            label: 'User Management',
            icon: Users,
            hasUnsavedChanges: unsavedChanges.has('userManagement'),
        },
        {
            id: 'system',
            label: 'System Settings',
            icon: Cog,
            hasUnsavedChanges: unsavedChanges.has('system'),
        },
    ];

    return (
        <Layout>
            <div className="h-full flex flex-col">
                {/* Breadcrumb Navigation */}
                <div className="p-8 pb-4">
                    <Breadcrumb>
                        <BreadcrumbList>
                            {/* <BreadcrumbItem>
                                <BreadcrumbLink href="#" onClick={() => setCurrentView('dashboard')} className="flex items-center gap-2">
                                    <Home className="w-3 h-3" />
                                    Dashboard
                                </BreadcrumbLink>
                            </BreadcrumbItem>
                            <BreadcrumbSeparator /> */}
                            <BreadcrumbItem>
                                <BreadcrumbPage>Settings</BreadcrumbPage>
                            </BreadcrumbItem>
                        </BreadcrumbList>
                    </Breadcrumb>
                </div>

                {/* Header */}
                <div className="px-8 pb-6">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <h1 className="text-gray-900 mb-2 text-2xl ">
                                System Settings
                            </h1>
                            <p className="text-gray-600 text-[16px]">
                                {/* Configure AI models, audit rules, data protection, and system preferences */}
                                Configure system preferences

                            </p>
                        </div>
                        <div className="flex items-center gap-2">
                            <SettingsIcon className="w-5 h-5 text-gray-400" />
                            {unsavedChanges.size > 0 && (
                                <Badge variant="destructive">
                                    {unsavedChanges.size} unsaved change{unsavedChanges.size !== 1 ? 's' : ''}
                                </Badge>
                            )}
                        </div>
                    </div>
                </div>

                {/* Settings Tabs */}
                <div className="flex-1 px-8 pb-8">
                    <SystemSettings
                                settings={settings.system}
                                onUpdate={(newSettings) => handleSettingsUpdate('system', newSettings)}
                                onSave={() => handleSettingsSave('system')}
                                hasUnsavedChanges={unsavedChanges.has('system')}
                            />
                    {/* <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full">
                        <TabsList className="grid w-full grid-cols-5 mb-6">
                            {tabs.map((tab) => {
                                const IconComponent = tab.icon;
                                return (
                                    <TabsTrigger
                                        key={tab.id}
                                        value={tab.id}
                                        className="flex items-center gap-2 relative text-sm"
                                    >
                                        <IconComponent className="w-4 h-4" />
                                        {tab.label}
                                        {tab.hasUnsavedChanges && (
                                            <div className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full"></div>
                                        )}
                                    </TabsTrigger>
                                );
                            })}
                        </TabsList>

                        <TabsContent value="llm-model" className="h-full overflow-y-auto ">
                            <LLMModelSettings
                                settings={settings.llmModel}
                                onUpdate={(newSettings) => handleSettingsUpdate('llmModel', newSettings)}
                                onSave={() => handleSettingsSave('llmModel')}
                                hasUnsavedChanges={unsavedChanges.has('llmModel')}
                            />
                        </TabsContent>

                        <TabsContent value="audit-workflow" className="h-full overflow-y-auto">
                            <AuditWorkflowSettings
                                settings={settings.auditWorkflow}
                                onUpdate={(newSettings) => handleSettingsUpdate('auditWorkflow', newSettings)}
                                onSave={() => handleSettingsSave('auditWorkflow')}
                                hasUnsavedChanges={unsavedChanges.has('auditWorkflow')}
                            />
                        </TabsContent>

                        <TabsContent value="redaction-policy" className="h-full overflow-y-auto">
                            <RedactionPolicySettings
                                settings={settings.redactionPolicy}
                                onUpdate={(newSettings) => handleSettingsUpdate('redactionPolicy', newSettings)}
                                onSave={() => handleSettingsSave('redactionPolicy')}
                                hasUnsavedChanges={unsavedChanges.has('redactionPolicy')}
                            />
                        </TabsContent>

                        <TabsContent value="user-management" className="h-full overflow-y-auto">
                            <UserManagementSettings
                                settings={settings.userManagement}
                                onUpdate={(newSettings) => handleSettingsUpdate('userManagement', newSettings)}
                                onSave={() => handleSettingsSave('userManagement')}
                                hasUnsavedChanges={unsavedChanges.has('userManagement')}
                            />
                        </TabsContent>

                        <TabsContent value="system" className="h-full overflow-y-auto">
                            <SystemSettings
                                settings={settings.system}
                                onUpdate={(newSettings) => handleSettingsUpdate('system', newSettings)}
                                onSave={() => handleSettingsSave('system')}
                                hasUnsavedChanges={unsavedChanges.has('system')}
                            />
                        </TabsContent>
                    </Tabs> */}
                </div>
            </div>
        </Layout>
    );
}